import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { maskCep, useCepLookup } from "../../lib/cep";
import { maskCpf, isValidCpf, CPF_INVALID_MSG } from "../../lib/cpf";
import { maskPhone, isValidPhone, PHONE_INVALID_MSG } from "../../lib/phone";
import { toast } from "sonner";
import { ShieldCheck, CheckCircle } from "@phosphor-icons/react";
import { CircleNotch } from "@phosphor-icons/react";

export default function NewClient() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "", cpf: "", rg: "", birth_date: "", phone: "", email: "",
    cep: "", street: "", number: "", complement: "", district: "", city: "", state: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const { cepLoading, cepError, cepSuccess, handleCepLookup } = useCepLookup(setForm);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const cpfInvalid = form.cpf.replace(/\D/g, "").length === 11 && !isValidCpf(form.cpf);
  const phoneInvalid = form.phone.replace(/\D/g, "").length >= 10 && !isValidPhone(form.phone);

  const submit = async (e) => {
    e.preventDefault();
    if (!isValidCpf(form.cpf)) { toast.error(CPF_INVALID_MSG); return; }
    if (!isValidPhone(form.phone)) { toast.error(PHONE_INVALID_MSG); return; }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name,
        cpf: form.cpf,
        rg: form.rg,
        birth_date: form.birth_date,
        phone: form.phone,
        email: form.email || null,
        address: {
          cep: form.cep, street: form.street, number: form.number,
          complement: form.complement, district: form.district, city: form.city, state: form.state,
        },
      };
      const { data } = await api.post("/clients", payload);
      toast.success("Cliente cadastrada!");
      nav(`/revenda/clientes/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao cadastrar");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="new-client-page">
      <div className="topbar">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C5A57]">Onboarding</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-1">Novo Cliente</h1>
        </div>
      </div>

      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          <section className="border border-[#EBE5DF] bg-white p-6">
            <h2 className="section-title mb-6">Dados pessoais</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Nome completo *</label>
                <input data-testid="client-name-input" required className="input-underline" value={form.name} onChange={upd("name")} />
              </div>
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">CPF *</label>
                <input data-testid="client-cpf-input" required className="input-underline" value={form.cpf} onChange={(e) => setForm({ ...form, cpf: maskCpf(e.target.value) })} placeholder="000.000.000-00" inputMode="numeric" maxLength={14} />
                {cpfInvalid && <div className="text-[10px] text-[#B4564F] mt-1" data-testid="client-cpf-error">{CPF_INVALID_MSG}</div>}
              </div>
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">RG</label>
                <input className="input-underline" value={form.rg} onChange={upd("rg")} />
              </div>
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Nascimento *</label>
                <input type="date" required className="input-underline" value={form.birth_date} onChange={upd("birth_date")} data-testid="client-birth-input" />
              </div>
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">WhatsApp *</label>
                <input data-testid="client-phone-input" required className="input-underline" value={form.phone} onChange={(e) => setForm({ ...form, phone: maskPhone(e.target.value) })} placeholder="(11) 99999-9999" inputMode="numeric" maxLength={16} />
                {phoneInvalid && <div className="text-[10px] text-[#B4564F] mt-1" data-testid="client-phone-error">{PHONE_INVALID_MSG}</div>}
              </div>
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">E-mail</label>
                <input type="email" className="input-underline" value={form.email} onChange={upd("email")} />
              </div>
            </div>
          </section>

          <section className="border border-[#EBE5DF] bg-white p-6">
            <h2 className="section-title mb-6">Endereco</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">CEP *</label>
                <div className="relative">
                  <input
                    className="input-underline"
                    required
                    value={form.cep}
                    onChange={(e) => { const v = maskCep(e.target.value); setForm({ ...form, cep: v }); handleCepLookup(v); }}
                    onBlur={(e) => handleCepLookup(e.target.value)}
                    placeholder="00000-000"
                    inputMode="numeric"
                    maxLength={9}
                    data-testid="client-cep-input"
                  />
                  {cepLoading && <CircleNotch size={16} className="absolute right-0 top-1 animate-spin text-[#DDA7A5]" data-testid="client-cep-loading" />}
                </div>
                {cepLoading && <div className="text-[10px] text-[#5C5A57] mt-1" data-testid="client-cep-searching">Buscando endereço...</div>}
                {cepSuccess && !cepLoading && <div className="text-[10px] text-[#3F6B45] mt-1 flex items-center gap-1" data-testid="client-cep-success"><CheckCircle size={12} weight="fill" /> Endereço localizado com sucesso.</div>}
                {cepError && <div className="text-[10px] text-[#B4564F] mt-1" data-testid="client-cep-error">{cepError}</div>}
              </div>
              <div className="md:col-span-2">
                <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Rua *</label>
                <input className="input-underline" required value={form.street} onChange={upd("street")} />
              </div>
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Numero *</label>
                <input className="input-underline" required value={form.number} onChange={upd("number")} />
              </div>
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Complemento</label>
                <input className="input-underline" value={form.complement} onChange={upd("complement")} />
              </div>
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Bairro *</label>
                <input className="input-underline" required value={form.district} onChange={upd("district")} />
              </div>
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Cidade *</label>
                <input className="input-underline" required value={form.city} onChange={upd("city")} />
              </div>
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">UF *</label>
                <input className="input-underline" required maxLength={2} value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })} />
              </div>
            </div>
          </section>
        </div>

        <aside className="space-y-6">
          <section className="border border-[#EBE5DF] bg-[#F5F0EB] p-6">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck size={20} weight="duotone" />
              <h2 className="section-title">Validacao biometrica</h2>
            </div>
            <p className="text-sm text-[#5C5A57] leading-relaxed">
              A <strong>selfie</strong> e a <strong>geolocalizacao</strong> nao sao capturadas
              neste cadastro. Elas serao coletadas diretamente da cliente quando ela acessar
              o link de assinatura da promissoria digital - garantindo prova legal de aceite
              feita pelo proprio cliente.
            </p>
            <div className="mt-4 pt-4 border-t border-[#EBE5DF] text-[10px] tracking-widest uppercase text-[#5C5A57]">
              Antifraude + LGPD compliant
            </div>
          </section>

          <button type="submit" className="brand w-full" disabled={submitting} data-testid="submit-client-btn">
            {submitting ? "Cadastrando..." : "Cadastrar Cliente"}
          </button>
        </aside>
      </form>
    </div>
  );
}
